import { autoAttributesPlugin } from '@growthbook/growthbook/plugins';
import { GrowthBook } from '@growthbook/growthbook-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import Cookies from 'universal-cookie';

const DEFAULT_DEV_REGEX = /localhost|(devint\.gcp|qa\.gcp)\.openx\.org/;
const cookies = new Cookies();

export type UseGrowthbookConfig = {
  apiHost: string;
  clientKey: string;
  appName?: string;
  attributes?: Record<string, unknown>;
  // for SSR
  isDevHostname?: (hostname: string) => boolean;
};

export interface UseGrowthbook {
  initialize: (hasStreaming?: boolean) => void;
  growthbook: GrowthBook | null;
  isInitialized: boolean;
}

export const useGrowthbook = (config: UseGrowthbookConfig): UseGrowthbook => {
  const gbRef = useRef<GrowthBook | null>(null);
  const [isInitialized, setInitialized] = useState(false);
  const [, forceRender] = useState({});

  const initialize = useCallback(
    (hasStreaming = false): void => {
      if (gbRef.current) return;

      try {
        const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
        const cookieValueDevMode = cookies.get('enableDevModeFeatureFlag');
        const hasCookieDevMode = cookieValueDevMode === '__TRUE__';
        const enableDevMode =
          hasCookieDevMode || (config.isDevHostname?.(hostname) ?? DEFAULT_DEV_REGEX.test(hostname));

        const gb = new GrowthBook({
          apiHost: config.apiHost,
          clientKey: config.clientKey,
          enableDevMode,
          attributes: {
            ...(config.appName ? { app: config.appName } : {}),
            ...(config.attributes ?? {}),
          },
          plugins: [autoAttributesPlugin()],
        });

        gb.init({
          streaming: hasStreaming,
          cacheSettings: {
            disableCache: true,
            backgroundSync: false,
            cacheKey: '',
            staleTTL: 0,
            maxAge: 0,
            maxEntries: 0,
            disableIdleStreams: false,
            idleStreamInterval: 0,
          },
        });
        gbRef.current = gb;
        setInitialized(true);
        forceRender({});
      } catch (e) {
        // biome-ignore lint/suspicious/noConsole: error logging is acceptable
        console.error('Failed to initialize GrowthBook:', e);
      }
    },
    [config]
  );

  useEffect(() => {
    return () => {
      gbRef.current?.destroy?.();
      gbRef.current = null;
    };
  }, []);

  return {
    initialize,
    growthbook: gbRef.current,
    isInitialized,
  };
};
